import PropTypes from "prop-types";
import ReportChart from "./ReportChart";
import { VictoryChart, VictoryContainer, VictoryAxis, VictoryLegend, VictoryLine, VictoryBar, VictoryGroup, VictoryScatter } from "victory";
import { abbreviateNumber, colors, monthYearStringToDate } from "../ReportsHelper";
import { ReportChartConsts } from "../ReportTooltips";
import NoData from "../NoData";

export default function QuotesNbusConversionChart({ data }) {
    if (!data || data?.length === 0) {
        return (
            <ReportChart {...ReportChartConsts('quotesNbusConversion')}>
                <NoData />
            </ReportChart>
        );
    }

    const labelColor = document.documentElement.getAttribute('data-gdstheme') === 'dark' ? "#ffffff" : "#000000";

    data = data.map(item => ({
        ...item,
        GroupingKey: monthYearStringToDate(item.GroupingKey)
    }));

    const completeMonthRange = (() => {
        const months = [];
        const allDates = data.map(item => item.GroupingKey);
        const current = new Date(Math.min(...allDates));
        const maxDate = new Date(Math.max(...allDates));
        while (current <= maxDate) {
            months.push(new Date(current));
            current.setMonth(current.getMonth() + 1);
        }
        const step = Math.ceil(months.length / 8);
        return months.filter((_, index) => index % step === 0);
    })();

    const groupColors = {
        "Quotes": colors[0],
        "New Business": colors[2],
        "Conversion": colors[1]
    };

    const maxPrimaryY = Math.max(...data.map(d => Math.max(d.Data.Quotes, d.Data.NewBusiness)));
    const maxConversion = Math.max(...data.map(d => d.Data.Conversion));
    const conversionScaleFactor = maxConversion === 0 ? 1 : maxPrimaryY / maxConversion;

    return (
        <ReportChart {...ReportChartConsts('quotesNbusConversion')}>
            <VictoryChart
                containerComponent={
                    <VictoryContainer
                        responsive={true}
                        preserveAspectRatio={"none"}
                    />
                }
            >
                <VictoryLegend
                    x={20}
                    orientation="horizontal"
                    gutter={20}
                    style={{ labels: { fontSize: 10, fill: labelColor } }}
                    data={Object.entries(groupColors).map(([metric, color]) => ({ name: metric, symbol: { fill: color } }))}
                />
                <VictoryAxis crossAxis
                    style={{
                        tickLabels: { fontSize: 10, fill: labelColor },
                        axis: { stroke: labelColor }
                    }}
                    tickValues={completeMonthRange
                        .filter((_, index, array) => {
                            const step = Math.ceil(array.length / 10);
                            return index % step === 0;
                        })}
                    tickFormat={(x) => new Date(x).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '\n')}
                />
                <VictoryAxis dependentAxis
                    domain={{ y: [0, Math.max(maxPrimaryY, maxConversion * conversionScaleFactor) * 1.2] }}
                    style={{
                        tickLabels: { fontSize: 10, fill: labelColor },
                        axis: { stroke: labelColor },
                        grid: { stroke: labelColor, size: 5 }
                    }}
                    tickFormat={(x) => `${abbreviateNumber(x)}`}
                />
                <VictoryAxis dependentAxis
                    orientation="right"
                    style={{
                        axis: { stroke: labelColor },
                        tickLabels: { fontSize: 10, fill: labelColor },
                    }}
                    tickFormat={(x) => `${(x / conversionScaleFactor * 100).toFixed(0)}%`}
                />
                <VictoryGroup
                    offset={8}
                    style={{ data: { width: 8 } }}
                >
                    <VictoryBar
                        data={data.map(d => ({
                            x: new Date(d.GroupingKey),
                            y: d.Data.Quotes
                        }))}
                        style={{ data: { fill: groupColors["Quotes"] } }}
                    />
                    <VictoryBar
                        data={data.map(d => ({
                            x: new Date(d.GroupingKey),
                            y: d.Data.NewBusiness
                        }))}
                        style={{ data: { fill: groupColors["New Business"] } }}
                    />
                </VictoryGroup>
                <VictoryLine
                    interpolation="cardinal"
                    data={data.map(d => ({
                        x: new Date(d.GroupingKey),
                        y: d.Data.Conversion * conversionScaleFactor
                    }))}
                    style={{ data: { stroke: groupColors["Conversion"] } }}
                />
                <VictoryScatter
                    size={2}
                    data={data.map(d => ({
                        x: new Date(d.GroupingKey),
                        y: d.Data.Conversion * conversionScaleFactor
                    }))}
                    style={{ data: { fill: groupColors["Conversion"] } }}
                />
            </VictoryChart>
        </ReportChart>
    );
}

QuotesNbusConversionChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        GroupingKey: PropTypes.string,
        Data: PropTypes.shape({
            Quotes: PropTypes.number,
            NewBusiness: PropTypes.number,
            Conversion: PropTypes.number
        })
    }))
};